import React, {useState} from 'react';
import {useDispatch} from 'react-redux';
import {useHistory, useLocation} from 'react-router-dom';
import queryString from 'query-string';
import AppBar from '@mui/material/AppBar';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Dialog from '@mui/material/Dialog';

import {confirmUser, resendEmailConfirmationCode} from '../../redux/slices/auth';
import {BluePillButton} from '../../common/components/BluePillButton';
import SecondaryAppBar from '../../lesson-builder/components/SecondayAppBar';
import {CONFIRMATION_CODE_LENGTH} from '../../constants';

import baseStyles from '../../css/Base.module.css';

export default function ConfirmUser() {
  const [code, setCode] = useState('');
  const [formErrors, setFormErrors] = useState({});
  const [errorDialogMessage, setErrorDialogMessage] = useState(null);

  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();

  const queryStrings = queryString.parse(location.search);

  function handleCodeChanged(event) {
    setCode(event.target.value);
  }

  function isSubmitButtonEnabled() {
    return !!code && code.length >= CONFIRMATION_CODE_LENGTH;
  }

  function validateForm() {
    const errors = {};

    if (!code) errors.code = 'This field is required.';
    else if (code.length < CONFIRMATION_CODE_LENGTH) errors.code = 'Invalid code.';

    setFormErrors(errors);

    return Object.keys(errors).length === 0;
  }

  async function handleResendCodePress() {
    const email = queryStrings.email;

    try {
      await dispatch(resendEmailConfirmationCode(email));
      setErrorDialogMessage('A new confirmation code was sent to your email address.');
    } catch {
      setErrorDialogMessage('An error occurred, try again.');
    }
  }

  async function handleSubmitForm(event) {
    event.preventDefault();
    const isFormValid = validateForm();

    if (isFormValid) {
      const email = queryStrings.email;
      let response;

      try {
        response = await dispatch(confirmUser(email, code));
      } catch (error) {
        console.log(`confirm user error: ${JSON.stringify(error)}`);

        const {code} = error;
        if (code === 'CodeMismatchException') {
          setFormErrors({code: 'Invalid confirmation code.'});
        } else {
          setErrorDialogMessage('An error occurred, try again.');
        }
      }

      console.log(`confirm user response: ${JSON.stringify(response)}`);

      if (response === 'SUCCESS') {
        history.push('/login');
      }
    }
  }

  function renderErrorDialog() {
    return (
      <Dialog open={!!errorDialogMessage} onClose={() => setErrorDialogMessage(null)}>
        <DialogTitle>Confirm Email</DialogTitle>
        <DialogContent>
          <DialogContentText>{errorDialogMessage}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setErrorDialogMessage(null)} color="primary" autoFocus>
            OK
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <>
      <AppBar position="sticky" className="bg-brandorange">
        <Toolbar variant="dense">
          <Typography variant="h6" className={baseStyles['f-g-1']}>
            Flamingo Learning
          </Typography>
        </Toolbar>
      </AppBar>
      <SecondaryAppBar title="Confirm Email" />

      {renderErrorDialog()}

      <form className="p-20" onSubmit={handleSubmitForm}>
        <Grid container spacing={3} item xl={3} lg={4} md={5} sm={7}>
          <Typography component="div" variant="body1" className="p-tb-10 p-lr-10">
            <Typography display="inline">A confirmation code was sent to</Typography>
            <Typography display="inline" className="fnt-bold">{` ${queryStrings.email}`}</Typography>
            <Typography display="inline">
              . If this email is not correct, <a href="/register">register again</a> with the correct email address.
            </Typography>
          </Typography>
          <Grid item xs={12}>
            <TextField
              variant="standard"
              margin="dense"
              label="Confirmation code"
              onChange={handleCodeChanged}
              value={code}
              error={!!formErrors.code}
              helperText={formErrors.code}
              autoFocus
              fullWidth
            />
          </Grid>

          <Grid item xs={6}>
            <Button onClick={handleResendCodePress}>Send new code</Button>
          </Grid>
          <Grid item xs={6} className={baseStyles['text-align-end']}>
            <BluePillButton type="submit" variant="outlined" disabled={!isSubmitButtonEnabled()}>
              SUBMIT
            </BluePillButton>
          </Grid>
        </Grid>
      </form>
    </>
  );
}
